import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api'
import Meldung from './Meldung'
import Meldungen from './Meldungen'
import { menge, zeitpunkt } from '../format'
import { useAktion, useLaden } from '../hooks'
import { RUECKFRAGESTATUS, SCHWERETEXT } from '../texte'
import type { Rueckfrage, Rueckfragestatus } from '../typen'

interface Eigenschaften {
  /** Nur die Fragen zu diesem Lauf. Ohne Angabe: alle, die noch zu erledigen sind. */
  importLaufId?: number
  /** Wird gerufen, wenn sich durch eine Antwort am Lauf etwas geaendert hat. */
  onGeaendert?: () => void
  /** Nur Fragen in diesem Stand - etwa `uebergangen` (beim Buchen ohne Antwort geschlossen, 02.11.2026). */
  status?: Rueckfragestatus
  /** Überschrift statt „Rückfragen“ */
  titel?: string
}


/**
 * Fragen des Systems an Frau Barth - und ihre Antworten in eigenen Worten.
 *
 * Drei Schritte je Frage, jeder sichtbar: sie tippt frei, der Agent macht daraus einen Vorschlag
 * in Klartext, und erst „Anwenden" aendert etwas. Danach wird der Lauf erneut geprueft.
 */
export default function Rueckfragen({ importLaufId, onGeaendert, status, titel = 'Rückfragen' }: Eigenschaften) {
  const [antworten, setAntworten] = useState<Record<number, string>>({})
  // Welche Frage gerade bearbeitet wird - nur ihre Knöpfe sind gesperrt, die anderen bleiben bedienbar.
  const [arbeitet, setArbeitet] = useState<number | null>(null)
  const [alleZeigen, setAlleZeigen] = useState(false)
  const { fehler, erfolg, setErfolg, ausfuehren } = useAktion()

  // Ohne Lauf liefert das Backend nur, was zu tun ist; mit Lauf alles - dann hier filtern,
  // damit „auch erledigte zeigen" ohne zweiten Aufruf geht.
  const { daten, setDaten, fehler: ladeFehler, neuLaden: laden } = useLaden(
    async () => {
      const werte = await api.rueckfragen(importLaufId, status)
      return importLaufId && !alleZeigen && !status ? werte.filter((f) => f.status === 'offen' || f.status === 'beantwortet') : werte
    },
    [importLaufId, alleZeigen, status],
  )
  const fragen = daten ?? []

  function ersetzen(neu: Rueckfrage) {
    setDaten((alt) => (alt ?? []).map((f) => (f.id === neu.id ? neu : f)))
  }

  /** Eine Aktion zu einer Frage: solange sie läuft, sind die Knöpfe dieser Frage gesperrt. */
  function jeFrage(frage: Rueckfrage, aktion: () => Promise<void>) {
    setArbeitet(frage.id)
    void ausfuehren(aktion).finally(() => setArbeitet(null))
  }

  function beantworten(frage: Rueckfrage) {
    const text = (antworten[frage.id] ?? '').trim()
    if (!text) return
    jeFrage(frage, async () => {
      ersetzen(await api.rueckfrageBeantworten(frage.id, text))
      setAntworten((alt) => ({ ...alt, [frage.id]: '' }))
    })
  }

  function anwenden(frage: Rueckfrage) {
    jeFrage(frage, async () => {
      const ergebnis = await api.rueckfrageAnwenden(frage.id)
      ersetzen(ergebnis.rueckfrage)
      const gescheitert = ergebnis.wirkung.filter((w) => 'fehler' in w)
      setErfolg(
        gescheitert.length > 0
          ? `Angewendet, aber ${gescheitert.length} Schritt(e) gingen nicht: ${gescheitert
              .map((w) => String(w.fehler))
              .join(' ')}`
          : ergebnis.lauf_status === 'gesperrt'
            ? 'Angewendet. Der Lauf bleibt gesperrt - es gibt weitere Befunde.'
            : ergebnis.lauf_status
              ? `Angewendet. Der Lauf ist jetzt ${ergebnis.lauf_status === 'offen' ? 'freigebbar' : ergebnis.lauf_status}.`
              : 'Angewendet.',
      )
      onGeaendert?.()
      laden()
    })
  }

  function verwerfen(frage: Rueckfrage) {
    jeFrage(frage, async () => {
      ersetzen(await api.rueckfrageVerwerfen(frage.id))
      onGeaendert?.()
    })
  }

  const offene = fragen.filter((f) => f.status === 'offen' || f.status === 'beantwortet')

  // Nach der letzten angewendeten Frage ist die Liste leer - die Meldung dazu muss trotzdem stehen.
  if (fragen.length === 0 && !ladeFehler && !fehler && !erfolg) {
    return importLaufId || status ? null : <p className="hinweis">Keine offenen Rückfragen.</p>
  }

  return (
    <section className="rueckfragen">
      <div className="leiste">
        <h3>
          {titel}
          {offene.length > 0 ? ` (${offene.length} offen)` : status ? ` (${fragen.length})` : ''}
        </h3>
        {importLaufId && (
          <label className="hinweis">
            <input type="checkbox" checked={alleZeigen} onChange={(e) => setAlleZeigen(e.target.checked)} />{' '}
            auch erledigte zeigen
          </label>
        )}
      </div>

      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />

      {fragen.map((frage) => (
        <article key={frage.id} className={`rueckfrage ${frage.schwere} ${frage.status}`}>
          <div className="rueckfrage-kopf">
            <span className={`status ${RUECKFRAGESTATUS[frage.status].klasse}`}>{RUECKFRAGESTATUS[frage.status].text}</span>
            {/* Was den Import sperrt, steht als Marke, nicht als grauer Kleintext - solange die Frage offen ist. */}
            {frage.schwere === 'fehler' && (frage.status === 'offen' || frage.status === 'beantwortet') && (
              <span className="status gesperrt">{SCHWERETEXT.fehler}</span>
            )}
            <span className="hinweis">
              {frage.schwere === 'fehler' && (frage.status === 'offen' || frage.status === 'beantwortet')
                ? zeitpunkt(frage.angelegt_am)
                : `${SCHWERETEXT[frage.schwere]} · ${zeitpunkt(frage.angelegt_am)}`}
              {!importLaufId && frage.import_lauf_id && (
                <>
                  {' '}
                  · <Link to={`/importe/${frage.import_lauf_id}`}>Import {frage.import_lauf_id}</Link>
                </>
              )}
            </span>
          </div>

          <p className="rueckfrage-frage">{frage.frage}</p>

          {frage.antwort && (
            <div className="rueckfrage-antwort">
              <b>Ihre Antwort</b>
              <p>{frage.antwort}</p>
            </div>
          )}

          {frage.status === 'beantwortet' && (
            <div className="rueckfrage-vorschlag">
              <b>
                Vorschlag
                {frage.sicherheit === 'unsicher' && <span className="status gesperrt">unsicher</span>}
              </b>
              {frage.erklaerung && <p>{frage.erklaerung}</p>}
              {frage.aktionen.length > 0 ? (
                <ul>
                  {frage.aktionen.map((aktion, index) => (
                    <li key={index}>{aktionText(aktion)}</li>
                  ))}
                </ul>
              ) : (
                <p className="hinweis">Keine Änderung vorgeschlagen.</p>
              )}
              {frage.rueckfrage_des_agenten && (
                <Meldung art="warnung" titel="Noch eine Frage">
                  {frage.rueckfrage_des_agenten}
                </Meldung>
              )}
            </div>
          )}

          {frage.status === 'erledigt' && frage.wirkung && (
            <div className="rueckfrage-wirkung">
              <b>Das ist geschehen</b>
              <ul>
                {(Array.isArray(frage.wirkung) ? frage.wirkung : [frage.wirkung]).map((w, index) => (
                  <li key={index}>{wirkungText(w)}</li>
                ))}
              </ul>
            </div>
          )}

          {(frage.status === 'offen' || frage.status === 'beantwortet' || frage.status === 'uebergangen') && (
            <>
              <textarea
                className="rueckfrage-eingabe"
                value={antworten[frage.id] ?? ''}
                onChange={(e) => setAntworten((alt) => ({ ...alt, [frage.id]: e.target.value }))}
                placeholder={
                  frage.status === 'beantwortet'
                    ? 'Falls der Vorschlag nicht passt: hier in anderen Worten ergänzen.'
                    : frage.status === 'uebergangen'
                      ? 'Der Lauf ist gebucht. Wer inzwischen weiß, worum es ging, trägt es hier nach - so kommt dieselbe Frage nicht wieder.'
                      : 'In eigenen Worten antworten - so, wie Sie es einer Kollegin sagen würden.'
                }
                rows={3}
              />
              <div className="leiste">
                <button
                  disabled={arbeitet === frage.id || !(antworten[frage.id] ?? '').trim()}
                  onClick={() => beantworten(frage)}
                >
                  {arbeitet === frage.id ? 'Wird gelesen …' : 'Antwort senden'}
                </button>
                {frage.status === 'beantwortet' && frage.aktionen.length > 0 && (
                  <button className="haupt" disabled={arbeitet === frage.id} onClick={() => anwenden(frage)}>
                    Vorschlag anwenden
                  </button>
                )}
                {frage.status !== 'uebergangen' && (
                  <button disabled={arbeitet === frage.id} onClick={() => verwerfen(frage)}>
                    Offen lassen
                  </button>
                )}
              </div>
            </>
          )}
        </article>
      ))}
    </section>
  )
}

/** Eine Handlung des Agenten als deutscher Satz - was passieren wuerde, nicht wie es heisst. */
function aktionText(aktion: Record<string, unknown>): string {
  const a = aktion as Record<string, string | number | boolean | Record<string, unknown> | string[] | null>
  const felder = (a.felder && typeof a.felder === 'object' ? (a.felder as Record<string, unknown>) : null)
  const feldliste = felder
    ? Object.entries(felder)
        .map(([k, v]) => `${k} = ${String(v)}`)
        .join(', ')
    : ''
  switch (a.aktion) {
    case 'alias':
      return `Artikelnummer ${a.fremdnummer} wird als zweite Nummer von ${a.artikelnummer} geführt.`
    case 'artikel_anlegen':
      return `Neuer Artikel ${a.artikelnummer} „${a.bezeichnung ?? ''}“ (${a.art ?? 'material'}, ${a.einheit ?? 'Stk'}${
        a.gebindegewicht_kg ? `, ${menge(Number(a.gebindegewicht_kg))} kg` : ''
      }${a.mvn ? `, MVN ${a.mvn}` : ''}${a.gebindegruppe ? `, Blatt ${a.gebindegruppe}` : ''}${
        a.aktiv === false ? ', nicht disponiert' : ''
      }).`
    case 'stammdaten':
      return `Stammdaten von ${a.artikelnummer} ändern: ${feldliste || 'nichts'}.`
    case 'position_aendern':
      return `Zeile(n) ${a.artikelnummer ? `von ${a.artikelnummer}` : Array.isArray(a.position_ids) ? a.position_ids.join(', ') : ''} ändern: ${feldliste || 'nichts'}.`
    case 'positionen_abwaehlen':
      return `Nicht buchen: ${Array.isArray(a.artikelnummern) ? a.artikelnummern.join(', ') : ''}${a.rolle ? ` (${a.rolle})` : ''}.`
    case 'dokumenttyp_setzen':
      return `Dokument als „${a.dokumenttyp}“ einordnen${a.rolle_fuer_alle ? `, alle Zeilen als ${a.rolle_fuer_alle}` : ''}${a.tag ? `, Tag ${a.tag}` : ''}.`
    case 'befund_akzeptieren':
      return `Befund „${a.regel}“ bewusst hinnehmen - der Lauf wird trotzdem freigebbar.`
    case 'lauf_verwerfen':
      return 'Den ganzen Import verwerfen.'
    case 'keine_aenderung':
      return 'Nichts ändern.'
    default:
      return JSON.stringify(aktion)
  }
}

function wirkungText(w: Record<string, unknown>): string {
  if ('fehler' in w) return `${String(w.aktion)}: nicht möglich - ${String(w.fehler)}`
  if ('hinweis' in w) return String(w.hinweis)
  const rest = Object.entries(w)
    .filter(([k]) => k !== 'aktion')
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(', ')
  return `${String(w.aktion ?? '')}${rest ? ` (${rest})` : ''}`
}
